/* eslint-disable */
// ConsolidatedInitiativesGantt.js
// -----------------------------------------------------------
// Visão consolidada (read-only) – granularidade + range datas
// -----------------------------------------------------------
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Tooltip,
  Typography,
  TextField,
  Button,
  Icon,
} from "@material-ui/core";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort, faSortUp, faSortDown, faFilter, faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { formatNumber } from "utils/formatNumberUtils";
import cx from "classnames";

  const CELL_PX = { month: 65, quarter: 70, year: 80 };
  const GANTT_ROW_HEIGHT = 35;

function ConsolidatedInitiativesGantt({ initiatives, classes, t, onSelectInitiative, userDetails }) {
/* ╭─ 0. CONFIGURAÇÃO DO MODO ────────────────────────────────╮ */
  // Determina o modo baseado na configuração da implementação
  const ganttMode = userDetails?.gantt_mode || 'with_capture';
  const hasCapture = ganttMode === 'with_capture';

/* ╭─ 1. CONTROLES ───────────────────────────────────────────╮ */
  const [viewMode, setViewMode] = useState("month");
  const handleMode = (_, m) => m && setViewMode(m);

  const [startFilter, setStartFilter] = useState("");
  const [endFilter, setEndFilter] = useState("");
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const clearFilters = () => {
    setStartFilter("");
    setEndFilter("");
  };

/* ╭─ 2. PROCESSAMENTO DE DATAS E FILTRO ────────────────────╮ */
  const toDate = (ym) => ym ? new Date(`${ym}-01`) : null;   // "2025-06" → 1/jun
  
  // Função para calcular datas baseadas nos deliverables
  const calculateDeliverablesDateRange = (deliverables) => {
    if (!deliverables || deliverables.length === 0) return { start: null, end: null };
    
    const validDeliverables = deliverables.filter(d => 
      d.planned_start_date && d.planned_end_date
    );
    
    if (validDeliverables.length === 0) return { start: null, end: null };
    
    const startDates = validDeliverables.map(d => new Date(d.planned_start_date));
    const endDates = validDeliverables.map(d => new Date(d.planned_end_date));
    
    return {
      start: new Date(Math.min(...startDates)).toISOString().split('T')[0],
      end: new Date(Math.max(...endDates)).toISOString().split('T')[0]
    };
  };

  // Processa as iniciativas com as datas corretas baseadas no modo
  const processedInitiatives = useMemo(() => {
    return initiatives.map(initiative => {
      const deliverablesDates = calculateDeliverablesDateRange(initiative.deliverables);
      
      let processedInitiative = { ...initiative };
      
      if (hasCapture) {
        // Modo 1: Com fase de captura
        // Fase de implementação baseada nos deliverables
        if (deliverablesDates.start && deliverablesDates.end) {
          processedInitiative.imp_start = deliverablesDates.start;
          processedInitiative.imp_end = deliverablesDates.end;
        }
        // Se não temos deliverables, usa as datas que já vêm da API (imp_start/imp_end)
        // que já são calculadas no backend com fallback para activities_start_date/activities_end_date
        
        // Fase de captura usa as datas que já vêm da API (cap_start/cap_end)
        // que já são calculadas no backend com fallback para capture_start_date/capture_end_date
      } else {
        // Modo 2: Sem fase de captura
        // Apenas fase de implementação baseada nos deliverables
        if (deliverablesDates.start && deliverablesDates.end) {
          processedInitiative.imp_start = deliverablesDates.start;
          processedInitiative.imp_end = deliverablesDates.end;
        }
        // Se não temos deliverables, usa as datas que já vêm da API (imp_start/imp_end)
        
        // Remove dados de captura
        processedInitiative.cap_start = null;
        processedInitiative.cap_end = null;
      }
      
      return processedInitiative;
    });
  }, [initiatives, hasCapture]);

  const filtered = useMemo(() => {
    const sFilter = toDate(startFilter);
    const eFilter = toDate(endFilter);
    return processedInitiatives.filter(r => {
      const hasImp = r.imp_start && r.imp_end;
      const hasCap = hasCapture && r.cap_start && r.cap_end;
      if (!hasImp && !hasCap) return false;

      const rowStart = new Date(hasImp ? r.imp_start : r.cap_start);
      const rowEnd = new Date(hasCap ? r.cap_end : r.imp_end);

      if (sFilter && rowStart < sFilter) return false;
      if (eFilter && rowEnd > eFilter) return false;
      return true;
    });
  }, [processedInitiatives, startFilter, endFilter, hasCapture]);

  const [sortCfg, setSortCfg] = useState({ key: "initiative", dir: "asc" });
  const toggleSort = (k) =>
    setSortCfg(p =>
      p.key === k ? { key: k, dir: p.dir === "asc" ? "desc" : "asc" }
        : { key: k, dir: "asc" }
    );
  const sortIcon = (k) =>
    sortCfg.key !== k
      ? <FontAwesomeIcon icon={faSort} className={classes.sortIcon} />
      : <FontAwesomeIcon
        icon={sortCfg.dir === "asc" ? faSortUp : faSortDown}
        className={`${classes.sortIcon} ${classes.activeSortIcon}`}
      />;

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortCfg.key], bv = b[sortCfg.key];
      if (typeof av === "string") { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return sortCfg.dir === "asc" ? -1 : 1;
      if (av > bv) return sortCfg.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortCfg]);

/* ╭─ 3. INTERVALO DA LINHA DO TEMPO ─────────────────────────╮ */
  const { timelineStart, timelineEnd } = useMemo(() => {
    if (!sorted.length) {
      const now = new Date();
      return { timelineStart: now, timelineEnd: now };
    }
    const dates = [];
    sorted.forEach(i =>
      ["imp_start", "imp_end", "cap_start", "cap_end"].forEach(k => i[k] && dates.push(new Date(i[k])))
    );
    let min = new Date(Math.min(...dates));
    let max = new Date(Math.max(...dates));

    if (viewMode === "month") {
      min.setMonth(min.getMonth() - 1); max.setMonth(max.getMonth() + 1);
      min.setDate(1); max.setDate(1);
    } else if (viewMode === "quarter") {
      min.setMonth(Math.floor(min.getMonth() / 3) * 3);
      max.setMonth(Math.floor(max.getMonth() / 3) * 3);
    } else { // year
      min.setMonth(0); max.setMonth(0);
      min.setFullYear(min.getFullYear() - 1);
      max.setFullYear(max.getFullYear() + 1);
    }
    return { timelineStart: min, timelineEnd: max };
  }, [sorted, viewMode]);

/* ╭─ 4. PERÍODOS HEADER ────────────────────────────────────╮ */
  const periods = useMemo(() => {
    const out = []; const cur = new Date(timelineStart);
    if (viewMode === "month") {
      while (cur <= timelineEnd) { out.push({ y: cur.getFullYear(), m: cur.getMonth() }); cur.setMonth(cur.getMonth() + 1); }
    } else if (viewMode === "quarter") {
      while (cur <= timelineEnd) { out.push({ y: cur.getFullYear(), q: Math.floor(cur.getMonth() / 3) + 1 }); cur.setMonth(cur.getMonth() + 3); }
    } else {
      while (cur <= timelineEnd) { out.push({ y: cur.getFullYear() }); cur.setFullYear(cur.getFullYear() + 1); }
    }
    return out;
  }, [timelineStart, timelineEnd, viewMode]);

/* helpers posição/largura ---------------------------------- */
  const diffPeriods = (d1, d2) => {
    if (viewMode === "month") return (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
    if (viewMode === "quarter") return (d2.getFullYear() - d1.getFullYear()) * 4 + Math.floor(d2.getMonth() / 3) - Math.floor(d1.getMonth() / 3);
    return d2.getFullYear() - d1.getFullYear();
  };
  const pxLeft = (date) => diffPeriods(timelineStart, new Date(date)) * CELL_PX[viewMode];
  const pxWidth = (s, e) => (diffPeriods(new Date(s), new Date(e)) + 1) * CELL_PX[viewMode];
  const colorByStatus = st => ({ red: "#E86C75", yellow: "#FFCB6B", green: "#6ECF93", gray: "#B0B0B0" }[st] || "#B0B0B0");

  // Today marker position calculation
  const todayMarkerPosition = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (today < timelineStart || today > timelineEnd) return null;

    const startPos = diffPeriods(timelineStart, today) + 1;

    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();

    let percentage;
    if (viewMode === "month") {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      percentage = (day - 1) / daysInMonth;
    } else if (viewMode === "quarter") {
      const qStartMonth = Math.floor(month / 3) * 3;
      const qStart = new Date(year, qStartMonth, 1);
      const qEnd = new Date(year, qStartMonth + 3, 0);
      const totalDays = (qEnd - qStart) / (1000 * 60 * 60 * 24) + 1;
      const elapsedDays = (today - qStart) / (1000 * 60 * 60 * 24);
      percentage = elapsedDays / totalDays;
    } else {
      const yearStart = new Date(year, 0, 1);
      const nextYear = new Date(year + 1, 0, 1);
      const totalDays = (nextYear - yearStart) / (1000 * 60 * 60 * 24);
      const elapsedDays = (today - yearStart) / (1000 * 60 * 60 * 24);
      percentage = elapsedDays / totalDays;
    }

    return startPos + percentage - 1;
  }, [viewMode, timelineStart, timelineEnd]);

/* ╭─ 5. RENDER ─────────────────────────────────────────────╮ */
  return (
    <div className={classes.ganttMaster} style={{ marginTop: "15px", marginLeft: "-10px", marginRight: "-10px" }}>
{/* ───────── CONTROLES TOPO ──────────────────────────────── */}
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 12,
          alignItems: "center", justifyContent: "space-between",
          margin: "15px 4px 4px 4px",
        }}
      >
        {/* granularidade */}
                 <ToggleButtonGroup value={viewMode} exclusive onChange={handleMode} size="small">
           <ToggleButton className={classes.toggleGanttDataFilter} value="month">{t("execution.monthly")}</ToggleButton>
           <ToggleButton className={classes.toggleGanttDataFilter} value="quarter">{t("execution.quarterly")}</ToggleButton>
           <ToggleButton className={classes.toggleGanttDataFilter} value="year">{t("execution.yearly")}</ToggleButton>
         </ToggleButtonGroup>

        {/* limpa filtros + range */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* CLEAR ALL */}
          <div style={{ position: "relative", display: "inline-block", marginRight: "3px" }}>
            <Tooltip title={t("filters.clear_all")}>
              <FontAwesomeIcon
                icon={faFilter}
                onClick={clearFilters}
                style={{ fontSize: "20px", color: "#666", cursor: "pointer" }}
              />
            </Tooltip>
            <div
              onClick={clearFilters}
              style={{
                position: "absolute",
                bottom: "1px",
                right: "-2px",
                backgroundColor: "transparent",
                color: "#666",
                fontSize: "11px",
                fontWeight: "bold",
                borderRadius: "50%",
                width: "10px",
                height: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </div>
          </div>

          {/* range month-only */}
          <TextField
            type="month" size="small" value={startFilter}
            onChange={e => setStartFilter(e.target.value)}
            label={t("execution.from_date")}
            classes={{ root: classes.textFieldRoot }}
            InputProps={{ classes: { input: classes.inputText }, style: { fontSize: 12 } }}
            InputLabelProps={{ classes: { root: classes.labelText }, shrink: true, style: { fontSize: 12 } }}
          />
          <span style={{ fontSize: 12 }}>–</span>
          <TextField
            type="month" size="small" value={endFilter}
            onChange={e => setEndFilter(e.target.value)}
            label={t("execution.to_date")}
            classes={{ root: classes.textFieldRoot }}
            InputProps={{ classes: { input: classes.inputText }, style: { fontSize: 12 } }}
            InputLabelProps={{ classes: { root: classes.labelText }, shrink: true, style: { fontSize: 12 } }}
          />
        </div>

        {/* legenda */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
          <span style={{ width: 22, height: 10, background: "#666", borderRadius: 2 }} />
          <Typography variant="caption">{t("execution.implementation")}</Typography>
          {hasCapture && (
            <>
              <span style={{
                width: 22, height: 10,
                background: "repeating-linear-gradient(45deg,#6666 0 6px,transparent 6px 12px)",
                border: "1px dashed #666", borderRadius: 2,
              }} />
              <Typography variant="caption">{t("results_kpi.capture")}</Typography>
            </>
          )}
        </div>
      </div>

{/* ───────── GRID ────────────────────────────────────────── */}
      {sorted.length === 0 && (
                 <div style={{ margin: "40px auto", textAlign: "center", color: "#666" }}>
           <Icon style={{ fontSize: 48, color: "#ddd" }}>calendar_today</Icon>
           <Typography variant="h6" style={{ marginTop: 8, color: "#aaa" }}>
             {t("execution.no_initiatives_for_filter")}
           </Typography>
           <Typography variant="body2" style={{ color: "#bbb" }}>
             {t("initiatives.try_adjusting_filters")}
           </Typography>
         </div>
      )}

      {sorted.length > 0 && (
        <div style={{ display: "flex" }}>
          {/* tabela esquerda */}
          <div className={classes.ganttTable} style={{ flex: "0 0 auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead className={classes.ganttTableHeader} style={{ height: GANTT_ROW_HEIGHT }}>
                <tr>
                  {[
                    { k: "initiative", label: t("initiatives.columns.initiative") },
                    { k: "leader", label: t("initiatives.columns.leader") },
                  ].map(c => (
                    <th key={c.k} onClick={() => toggleSort(c.k)} className={classes.ganttTableColumns}>
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}>
                        <span>{c.label}</span>
                        <span>{sortIcon(c.k)}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr
                    key={r.id}
                    style={{
                      height: GANTT_ROW_HEIGHT,
                      borderBottom: "1px solid #f0f0f0",
                      backgroundColor: hoveredRowId === r.id ? "#f5f5f5" : "transparent",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={() => setHoveredRowId(r.id)}
                    onMouseLeave={() => setHoveredRowId(null)}
                  >
                    <td className={classes.ganttTableBody1st}>
                      <Tooltip title={r.initiative} arrow>
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "140px",
                            width: "100%",
                            display: "block",
                            cursor: "pointer",
                            textDecoration: "underline",
                            textDecorationColor: "#aaa",
                            textUnderlineOffset: "2px",
                          }}
                          onClick={() => onSelectInitiative && onSelectInitiative(r.id, "execution")}
                        >
                          {r.initiative}
                        </span>
                      </Tooltip>
                    </td>
                    <td className={classes.ganttTableBody}>{r.leader}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* timeline */}
          <div style={{ flex: 1, overflowX: "auto" }}>
            <div style={{
              position: "relative",
              minWidth: `${periods.length * CELL_PX[viewMode]}px`,
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${periods.length}, minmax(${CELL_PX[viewMode]}px, 1fr))`,
                gridTemplateRows: `${GANTT_ROW_HEIGHT}px repeat(${sorted.length}, ${GANTT_ROW_HEIGHT}px)`,
              }}>
                {/* header períodos */}
                {periods.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      gridColumn: `${i + 1} / span 1`,
                      gridRow: "1 / 1",
                      textAlign: "center",
                      fontSize: "11px",
                      fontWeight: "bold",
                      color: "#fff",
                      backgroundColor: "#555555",
                      borderBottom: "1px solid #e0e0e0",
                      borderRight: "1px solid #e0e0e0",
                      padding: "4px 2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: "0",
                    }}
                  >
                    {viewMode === "month" && `${t(`months.${p.m + 1}`).substring(0, 3)}'${String(p.y).slice(-2)}`}
                    {viewMode === "quarter" && `Q${p.q} ${p.y}`}
                    {viewMode === "year" && p.y}
                  </div>
                ))}

                {/* linhas gantt */}
                {sorted.map((r, rowIndex) => {
                  const colour = colorByStatus(r.status);
                  const hasImp = r.imp_start && r.imp_end;
                  const hasCap = hasCapture && r.cap_start && r.cap_end;
                  if (!hasImp && !hasCap) return null;

                  const baseS = hasImp ? r.imp_start : r.cap_start;
                  const baseE = hasImp ? r.imp_end : r.cap_end;

                  // No modo sem captura, não há extensões de captura
                  const leftExt = hasCapture && hasImp && hasCap && new Date(r.cap_start) < new Date(r.imp_start)
                    ? { s: r.cap_start, e: r.imp_start } : null;
                  const rightExt = hasCapture && hasImp && hasCap && new Date(r.cap_end) > new Date(r.imp_end)
                    ? { s: r.imp_end, e: r.cap_end } : null;

                  const fullS = leftExt ? leftExt.s : baseS;
                  const fullE = rightExt ? rightExt.e : baseE;

                  /* se o width é pequeno (< 60px) colocamos rótulo fora */
                  const barWidth = pxWidth(baseS, baseE);
                  const labelOutside = barWidth < 60;

                  const barStyle = hasImp
                    ? { backgroundColor: colour }   // implementação ⟶ cor sólida
                    : {                             // só captura ⟶ padrão tracejado
                      background: `repeating-linear-gradient(
                                  45deg, ${colour}55 0 8px, transparent 8px 16px)`,
                      border: `2px dashed ${colour}`,
                    };

                  const isHovered = hoveredRowId === r.id;

                  return (
                    <div
                      key={r.id}
                      style={{
                        gridColumn: `${pxLeft(baseS) / CELL_PX[viewMode] + 1} / ${pxLeft(baseE) / CELL_PX[viewMode] + 2}`,
                        gridRow: `${rowIndex + 2.5} / span 1`,
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        padding: "2px 0",
                        animation: `slideIn 0.5s ${rowIndex * 0.05}s ease-out backwards`,
                        zIndex: isHovered ? 20 : 1,
                      }}
                      onMouseEnter={() => setHoveredRowId(r.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    >
                      {/* Extensões de captura - só renderiza se hasCapture for true */}
                      {hasCapture && [leftExt, rightExt].map((ext, idx) => ext && (
                        <Tooltip key={idx} title={`${t("results_kpi.capture")}: ${ext.s} → ${ext.e}`} arrow>
                          <div
                            onClick={() => onSelectInitiative && onSelectInitiative(r.id, "execution")}
                            style={{
                              position: "absolute",
                              left: pxLeft(ext.s), 
                              width: pxWidth(ext.s, ext.e),
                              height: "100%", 
                              top: "0%",
                              borderRadius: 4,
                              background: `repeating-linear-gradient(45deg,${colour}55 0 8px,transparent 8px 16px)`,
                              border: `2px dashed ${colour}`,
                              cursor: "pointer", 
                              zIndex: 1,
                            }}
                          />
                        </Tooltip>
                      ))}

                      <Tooltip
                        title={
                          <span style={{ whiteSpace: "pre-line" }}>
                            {hasImp ? `${t("execution.implementation")}: ${r.imp_start} → ${r.imp_end}\n` : ""}
                            {hasCapture && hasCap ? `${t("results_kpi.capture")}: ${r.cap_start} → ${r.cap_end}` : ""}
                          </span>
                        }
                        arrow
                      >
                        <div
                          onClick={() => onSelectInitiative && onSelectInitiative(r.id, "execution")}
                          style={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: hasImp ? colour : `${colour}40`,
                            color: "#ffffff",
                            cursor: "pointer",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 8px",
                            fontSize: "11px",
                            overflow: "hidden",
                            fontWeight: 400,
                            transition: "all 0.2s ease-out",
                            filter: isHovered ? "brightness(1.1)" : "brightness(1)",
                            ...barStyle,
                          }}
                        >
                          {/* Removed initiative name from here - only shown in tooltip */}
                        </div>
                      </Tooltip>
                    </div>
                  );
                })}

                {/* Grid Lines & Hover effect */}
                {sorted.map((r, rowIndex) =>
                  periods.map((__, periodIndex) => (
                    <div
                      key={`${r.id}-${periodIndex}`}
                                              style={{
                          gridColumn: `${periodIndex + 1} / span 1`,
                          gridRow: `${rowIndex + 2.5} / span 1`,
                          borderRight: "1px solid #f0f0f0",
                          borderBottom: "1px solid #f0f0f0",
                          zIndex: -1,
                          backgroundColor: hoveredRowId === r.id ? "#f5f5f5" : "transparent",
                          transition: "background-color 0.2s ease",
                        }}
                      onMouseEnter={() => setHoveredRowId(r.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                    />
                  )),
                )}
              </div>

              {/* Today Marker */}
              {todayMarkerPosition && (
                <div
                  style={{
                    position: "absolute",
                    top: "28px",
                    bottom: 0,
                    left: `calc((100% / ${periods.length}) * ${todayMarkerPosition})`,
                    width: "2px",
                    backgroundColor: "red",
                    zIndex: 10,
                    opacity: 0.7,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -8,
                      left: -3,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "red",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyframes style */}
      <style>
        {` @keyframes slideIn { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } } `}
      </style>
    </div>
  );
}

ConsolidatedInitiativesGantt.propTypes = {
  initiatives: PropTypes.array.isRequired,
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onSelectInitiative: PropTypes.func,
  userDetails: PropTypes.object,
};

export default ConsolidatedInitiativesGantt;
